<template>
  <Teleport to="body">
    <div v-if="mostrar" class="modal-overlay" @click.self="fecharModal">
      <div :class="['modal-content', { 'modal-fechando': animandoFechamento }]">
        <!-- Header -->
        <div class="modal-header">
          <h2>{{ modoEdicao ? 'Editar Lembrete' : 'Novo Lembrete Automático' }}</h2>
          <button class="btn-close" type="button" @click="fecharModal">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Progress Steps -->
        <div class="steps-indicator">
          <div
            v-for="step in 4"
            :key="step"
            class="step"
            :class="{ active: passoAtual >= step, current: passoAtual === step }"
          >
            <span class="step-number">{{ step }}</span>
            <span class="step-label">{{ getStepLabel(step) }}</span>
          </div>
        </div>

        <!-- Body -->
        <div class="modal-body">
          <!-- Passo 1: Informações Básicas -->
          <div v-if="passoAtual === 1" class="step-content">
            <div class="form-group">
              <label>Nome do lembrete <span class="required">*</span></label>
              <input
                v-model="form.nome"
                type="text"
                class="form-input"
                placeholder="Ex: Feliz Aniversário"
                maxlength="150"
              />
            </div>

            <div class="form-group">
              <label>Descrição</label>
              <textarea
                v-model="form.descricao"
                class="form-input"
                placeholder="Descrição opcional..."
                rows="2"
                maxlength="500"
              ></textarea>
            </div>

            <div class="form-group">
              <label>Tipo de lembrete <span class="required">*</span></label>
              <div class="tipos-grid">
                <div
                  v-for="(config, tipo) in tiposConfig"
                  :key="tipo"
                  class="tipo-card"
                  :class="{ selected: form.tipo === tipo }"
                  :style="form.tipo === tipo ? { borderColor: config.cor } : {}"
                  @click="form.tipo = tipo"
                >
                  <i :class="config.icon" :style="{ color: config.cor }"></i>
                  <span class="tipo-label">{{ config.label }}</span>
                  <span class="tipo-desc">{{ config.descricao }}</span>
                </div>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>Canal <span class="required">*</span></label>
                <div class="canal-options">
                  <label class="radio-card" :class="{ selected: form.canal === 'email' }">
                    <input v-model="form.canal" type="radio" value="email" />
                    <i class="fas fa-envelope"></i>
                    <span>Email</span>
                  </label>
                  <label class="radio-card" :class="{ selected: form.canal === 'whatsapp' }">
                    <input v-model="form.canal" type="radio" value="whatsapp" />
                    <i class="fab fa-whatsapp"></i>
                    <span>WhatsApp</span>
                  </label>
                </div>
              </div>

              <div class="form-group">
                <label>Template <span class="required">*</span></label>
                <select v-model="form.template_id" class="form-input">
                  <option :value="null">Selecione um template</option>
                  <option
                    v-for="template in templatesFiltrados"
                    :key="template.id"
                    :value="template.id"
                  >
                    {{ template.nome }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <!-- Passo 2: Configuração Específica -->
          <div v-if="passoAtual === 2" class="step-content">
            <div class="config-header">
              <i :class="tiposConfig[form.tipo]?.icon" :style="{ color: tiposConfig[form.tipo]?.cor }"></i>
              <span>Configuração: {{ tiposConfig[form.tipo]?.label }}</span>
            </div>

            <!-- Hora de envio (comum a todos) -->
            <div class="form-group">
              <label>Horário de envio <span class="required">*</span></label>
              <input v-model="form.configuracao.hora_envio" type="time" class="form-input time-input" />
              <span class="form-hint">O lembrete será processado neste horário</span>
            </div>

            <!-- Configuração: Boas-vindas -->
            <div v-if="form.tipo === 'boas_vindas'" class="form-group">
              <label>Dias após o cadastro <span class="required">*</span></label>
              <input
                v-model.number="form.configuracao.dias_apos_cadastro"
                type="number"
                class="form-input"
                min="1"
                max="365"
                placeholder="7"
              />
              <span class="form-hint">O lembrete será enviado X dias após a data de cadastro do paciente</span>
            </div>

            <!-- Configuração: Inatividade -->
            <div v-if="form.tipo === 'inatividade'" class="form-group">
              <label>Dias sem interação <span class="required">*</span></label>
              <input
                v-model.number="form.configuracao.dias_sem_interacao"
                type="number"
                class="form-input"
                min="1"
                max="365"
                placeholder="30"
              />
              <span class="form-hint">Pacientes que não receberam notificações há X dias</span>
            </div>

            <!-- Configuração: Recorrente -->
            <div v-if="form.tipo === 'recorrente'">
              <div class="form-group">
                <label>Frequência <span class="required">*</span></label>
                <div class="frequencia-options">
                  <label
                    v-for="(config, freq) in frequenciasConfig"
                    :key="freq"
                    class="radio-card"
                    :class="{ selected: form.configuracao.frequencia === freq }"
                  >
                    <input v-model="form.configuracao.frequencia" type="radio" :value="freq" />
                    <span class="freq-label">{{ config.label }}</span>
                    <span class="freq-desc">{{ config.descricao }}</span>
                  </label>
                </div>
              </div>

              <div v-if="form.configuracao.frequencia === 'semanal'" class="form-group">
                <label>Dia da semana</label>
                <select v-model.number="form.configuracao.dia_semana" class="form-input">
                  <option v-for="dia in diasSemana" :key="dia.value" :value="dia.value">
                    {{ dia.label }}
                  </option>
                </select>
              </div>

              <div v-if="form.configuracao.frequencia === 'mensal'" class="form-group">
                <label>Dia do mês</label>
                <select v-model.number="form.configuracao.dia_mes" class="form-input">
                  <option v-for="dia in 28" :key="dia" :value="dia">
                    Dia {{ dia }}
                  </option>
                </select>
                <span class="form-hint">Recomenda-se usar até dia 28 para evitar problemas em meses curtos</span>
              </div>
            </div>

            <!-- Aniversário: apenas hora -->
            <div v-if="form.tipo === 'aniversario'" class="info-box">
              <i class="fas fa-info-circle"></i>
              <p>
                O lembrete de aniversário será enviado automaticamente para pacientes que
                fazem aniversário no dia, com base no campo "Data de Nascimento" (dn).
              </p>
            </div>
          </div>

          <!-- Passo 3: Filtros de Destinatários -->
          <div v-if="passoAtual === 3" class="step-content">
            <h3 class="step-title">Filtrar Destinatários</h3>
            <p class="step-description">
              Selecione os filtros para definir quais pacientes receberão este lembrete.
              Apenas pacientes que aceitaram receber {{ form.canal === 'email' ? 'emails' : 'WhatsApp' }} serão incluídos.
            </p>

            <div class="filtros-sections">
              <!-- UFs -->
              <div class="form-group">
                <div class="filtro-header">
                  <label>Estados (UF)</label>
                  <button
                    v-if="form.filtros.uf.length > 0"
                    type="button"
                    class="btn-limpar-filtro"
                    @click="form.filtros.uf = []"
                  >
                    Limpar
                  </button>
                  <span v-else class="filtro-hint">Todos selecionados</span>
                </div>
                <div class="chips-grid chips-uf">
                  <button
                    v-for="uf in ufs"
                    :key="uf.sigla"
                    type="button"
                    class="chip"
                    :class="{ selected: form.filtros.uf.includes(uf.sigla) }"
                    :title="uf.nome"
                    @click="toggleFiltroUF(uf.sigla)"
                  >
                    {{ uf.sigla }}
                  </button>
                </div>
              </div>

              <!-- Sistema Inscrito -->
              <div class="form-group">
                <div class="filtro-header">
                  <label>Sistema Inscrito</label>
                  <button
                    v-if="form.filtros.sistema_inscrito.length > 0"
                    type="button"
                    class="btn-limpar-filtro"
                    @click="form.filtros.sistema_inscrito = []"
                  >
                    Limpar
                  </button>
                  <span v-else class="filtro-hint">Todos selecionados</span>
                </div>
                <div class="chips-grid chips-sistema">
                  <button
                    v-for="sistema in sistemas"
                    :key="sistema"
                    type="button"
                    class="chip chip-sistema"
                    :class="{ selected: form.filtros.sistema_inscrito.includes(sistema) }"
                    @click="toggleFiltroSistema(sistema)"
                  >
                    {{ sistema }}
                  </button>
                </div>
              </div>

              <!-- Sexo + Idade lado a lado -->
              <div class="filtros-row">
                <div class="form-group">
                  <div class="filtro-header">
                    <label>Sexo</label>
                    <button
                      v-if="form.filtros.sexo.length > 0"
                      type="button"
                      class="btn-limpar-filtro"
                      @click="form.filtros.sexo = []"
                    >
                      Limpar
                    </button>
                  </div>
                  <div class="chips-grid">
                    <button
                      type="button"
                      class="chip chip-sexo"
                      :class="{ selected: form.filtros.sexo.includes('F') }"
                      @click="toggleFiltroSexo('F')"
                    >
                      Feminino
                    </button>
                    <button
                      type="button"
                      class="chip chip-sexo"
                      :class="{ selected: form.filtros.sexo.includes('M') }"
                      @click="toggleFiltroSexo('M')"
                    >
                      Masculino
                    </button>
                    <button
                      type="button"
                      class="chip chip-sexo"
                      :class="{ selected: form.filtros.sexo.includes('O') }"
                      @click="toggleFiltroSexo('O')"
                    >
                      Outro
                    </button>
                  </div>
                </div>

                <div class="form-group">
                  <label>Faixa Etária</label>
                  <div class="idade-range">
                    <input
                      v-model.number="form.filtros.idade_min"
                      type="number"
                      class="form-input-small"
                      placeholder="Min"
                      min="0"
                    />
                    <span>até</span>
                    <input
                      v-model.number="form.filtros.idade_max"
                      type="number"
                      class="form-input-small"
                      placeholder="Max"
                      min="0"
                    />
                    <span>anos</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Contador de destinatários -->
            <div class="destinatarios-count" :class="{ loading: contandoDestinatarios }">
              <div class="count-box">
                <span v-if="contandoDestinatarios" class="count-loading">
                  <i class="fas fa-spinner fa-spin"></i> Contando...
                </span>
                <template v-else-if="contagemDestinatarios !== null">
                  <span class="count-number">{{ contagemDestinatarios.total_base }}</span>
                  <span class="count-label">
                    pacientes no público-alvo
                    <span v-if="form.tipo !== 'recorrente'" class="count-today">
                      ({{ contagemDestinatarios.total_hoje }} elegíveis hoje)
                    </span>
                  </span>
                </template>
                <template v-else>
                  <span class="count-number">?</span>
                  <span class="count-label">Clique para contar</span>
                </template>
              </div>
              <button type="button" class="btn-refresh" :disabled="contandoDestinatarios" @click="atualizarContagem">
                <i class="fas fa-sync-alt" :class="{ 'fa-spin': contandoDestinatarios }"></i>
                {{ contandoDestinatarios ? 'Contando...' : 'Atualizar contagem' }}
              </button>
            </div>
          </div>

          <!-- Passo 4: Revisão -->
          <div v-if="passoAtual === 4" class="step-content">
            <h3 class="review-title">Resumo do Lembrete</h3>

            <div class="review-grid">
              <div class="review-item">
                <label>Nome</label>
                <span>{{ form.nome }}</span>
              </div>

              <div class="review-item">
                <label>Tipo</label>
                <span class="tipo-badge" :style="getTipoStyle(form.tipo)">
                  <i :class="tiposConfig[form.tipo]?.icon"></i>
                  {{ tiposConfig[form.tipo]?.label }}
                </span>
              </div>

              <div class="review-item">
                <label>Canal</label>
                <span class="canal-badge" :class="form.canal">
                  <i :class="form.canal === 'email' ? 'fas fa-envelope' : 'fab fa-whatsapp'"></i>
                  {{ form.canal === 'email' ? 'Email' : 'WhatsApp' }}
                </span>
              </div>

              <div class="review-item">
                <label>Template</label>
                <span>{{ getTemplateNome(form.template_id) }}</span>
              </div>

              <div class="review-item full-width">
                <label>Configuração</label>
                <span>{{ getDescricaoConfiguracao(form) }}</span>
              </div>

              <div class="review-item full-width">
                <label>Filtros aplicados</label>
                <div class="filtros-resumo">
                  <span v-if="form.filtros.uf.length > 0">
                    UFs: {{ form.filtros.uf.join(', ') }}
                  </span>
                  <span v-if="form.filtros.sistema_inscrito.length > 0">
                    Sistemas: {{ form.filtros.sistema_inscrito.join(', ') }}
                  </span>
                  <span v-if="form.filtros.sexo.length > 0">
                    Sexo: {{ form.filtros.sexo.map(s => getSexoLabel(s)).join(', ') }}
                  </span>
                  <span v-if="form.filtros.idade_min || form.filtros.idade_max">
                    Idade: {{ form.filtros.idade_min || 0 }} - {{ form.filtros.idade_max || '∞' }} anos
                  </span>
                  <span v-if="!temFiltros" class="sem-filtros">Nenhum filtro (todos os pacientes)</span>
                </div>
              </div>

              <div class="review-item">
                <label>Destinatários base</label>
                <span class="destino-count">{{ contagemDestinatarios?.total_base || '?' }}</span>
              </div>

              <div class="review-item">
                <label>Status inicial</label>
                <span class="status-badge ativo">
                  <i class="fas fa-check"></i> Ativo
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <button v-if="passoAtual > 1" class="btn-secondary" type="button" @click="voltarPasso">
            <i class="fas fa-arrow-left"></i> Voltar
          </button>
          <button class="btn-secondary" type="button" @click="fecharModal">
            Cancelar
          </button>
          <button
            v-if="passoAtual < 4"
            class="btn-primary"
            type="button"
            :disabled="!podeAvancar"
            @click="avancarPasso"
          >
            Próximo <i class="fas fa-arrow-right"></i>
          </button>
          <button
            v-else
            class="btn-primary btn-save"
            type="button"
            :disabled="salvando"
            @click="salvar"
          >
            <i v-if="salvando" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-save"></i>
            {{ modoEdicao ? 'Salvar' : 'Criar Lembrete' }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useNotificationReminders } from '@/composables/useNotificationReminders'
import { listaUFs } from '@/utils/ufs'
import { useSistemasInscritos } from '@/composables/useSistemasInscritos'

// Carregar sistemas inscritos da API
const { sistemas: sistemasInscritos, carregar: carregarSistemas } = useSistemasInscritos()
carregarSistemas()

const props = defineProps({
  mostrar: { type: Boolean, default: false },
  modoEdicao: { type: Boolean, default: false },
  lembrete: { type: Object, default: null },
  templates: { type: Array, default: () => [] },
})

const emit = defineEmits(['fechar', 'salvar'])

const { tiposConfig, frequenciasConfig, diasSemana, getDescricaoConfiguracao } = useNotificationReminders()

const animandoFechamento = ref(false)
const passoAtual = ref(1)
const salvando = ref(false)
const contandoDestinatarios = ref(false)
const contagemDestinatarios = ref(null)

const form = ref({
  nome: '',
  descricao: '',
  tipo: 'aniversario',
  template_id: null,
  canal: 'email',
  filtros: {
    uf: [],
    sistema_inscrito: [],
    sexo: [],
    idade_min: null,
    idade_max: null,
  },
  configuracao: {
    hora_envio: '09:00',
    dias_apos_cadastro: 7,
    dias_sem_interacao: 30,
    frequencia: 'semanal',
    dia_semana: 1,
    dia_mes: 1,
  },
  ativo: true,
})

const ufs = listaUFs
const sistemas = sistemasInscritos // Reactive ref da API
const sexos = [
  { value: 'F', label: 'Feminino' },
  { value: 'M', label: 'Masculino' },
  { value: 'O', label: 'Outro' },
]

// Funções de toggle para filtros
function toggleFiltroUF(sigla) {
  const idx = form.value.filtros.uf.indexOf(sigla)
  if (idx >= 0) {
    form.value.filtros.uf.splice(idx, 1)
  } else {
    form.value.filtros.uf.push(sigla)
  }
}

function toggleFiltroSistema(sistema) {
  const idx = form.value.filtros.sistema_inscrito.indexOf(sistema)
  if (idx >= 0) {
    form.value.filtros.sistema_inscrito.splice(idx, 1)
  } else {
    form.value.filtros.sistema_inscrito.push(sistema)
  }
}

function toggleFiltroSexo(sexo) {
  const idx = form.value.filtros.sexo.indexOf(sexo)
  if (idx >= 0) {
    form.value.filtros.sexo.splice(idx, 1)
  } else {
    form.value.filtros.sexo.push(sexo)
  }
}

// Watch para carregar dados do lembrete em edição
watch(
  () => props.lembrete,
  (newVal) => {
    if (newVal) {
      form.value = {
        ...newVal,
        filtros: {
          uf: [],
          sistema_inscrito: [],
          sexo: [],
          idade_min: null,
          idade_max: null,
          ...newVal.filtros,
        },
        configuracao: {
          hora_envio: '09:00',
          dias_apos_cadastro: 7,
          dias_sem_interacao: 30,
          frequencia: 'semanal',
          dia_semana: 1,
          dia_mes: 1,
          ...newVal.configuracao,
        },
      }
      passoAtual.value = 1
      contagemDestinatarios.value = null
    }
  },
  { immediate: true }
)

// Reset ao abrir modal para criar
watch(
  () => props.mostrar,
  (val) => {
    if (val && !props.modoEdicao) {
      form.value = {
        nome: '',
        descricao: '',
        tipo: 'aniversario',
        template_id: null,
        canal: 'email',
        filtros: { uf: [], sistema_inscrito: [], sexo: [], idade_min: null, idade_max: null },
        configuracao: {
          hora_envio: '09:00',
          dias_apos_cadastro: 7,
          dias_sem_interacao: 30,
          frequencia: 'semanal',
          dia_semana: 1,
          dia_mes: 1,
        },
        ativo: true,
      }
      passoAtual.value = 1
      contagemDestinatarios.value = null
    }
  }
)

const templatesFiltrados = computed(() => {
  return props.templates.filter((t) => t.canal === form.value.canal || t.canal === 'ambos')
})

const podeAvancar = computed(() => {
  if (passoAtual.value === 1) {
    return form.value.nome.trim() && form.value.tipo && form.value.canal && form.value.template_id
  }
  if (passoAtual.value === 2) {
    if (!form.value.configuracao.hora_envio) return false
    if (form.value.tipo === 'boas_vindas' && !form.value.configuracao.dias_apos_cadastro) return false
    if (form.value.tipo === 'inatividade' && !form.value.configuracao.dias_sem_interacao) return false
    if (form.value.tipo === 'recorrente' && !form.value.configuracao.frequencia) return false
    return true
  }
  return true
})

const temFiltros = computed(() => {
  const f = form.value.filtros
  return (
    f.uf.length > 0 ||
    f.sistema_inscrito.length > 0 ||
    f.sexo.length > 0 ||
    f.idade_min ||
    f.idade_max
  )
})

function getStepLabel(step) {
  const labels = ['Info', 'Configuração', 'Filtros', 'Revisar']
  return labels[step - 1]
}

function getTipoStyle(tipo) {
  const config = tiposConfig[tipo]
  if (!config) return {}
  return { background: config.corBg, color: config.cor }
}

function getTemplateNome(id) {
  return props.templates.find((t) => t.id === id)?.nome || 'Não selecionado'
}

function getSexoLabel(value) {
  return sexos.find((s) => s.value === value)?.label || value
}

function avancarPasso() {
  if (passoAtual.value < 4 && podeAvancar.value) {
    passoAtual.value++
    if (passoAtual.value === 3) {
      atualizarContagem()
    }
  }
}

function voltarPasso() {
  if (passoAtual.value > 1) {
    passoAtual.value--
  }
}

async function atualizarContagem() {
  contandoDestinatarios.value = true
  try {
    const { useNotificationReminders } = await import('@/composables/useNotificationReminders')
    const { contarDestinatarios } = useNotificationReminders()

    const result = await contarDestinatarios({
      tipo: form.value.tipo,
      canal: form.value.canal,
      filtros: form.value.filtros,
      configuracao: form.value.configuracao,
    })
    contagemDestinatarios.value = result
  } catch {
    contagemDestinatarios.value = { total_base: 0, total_hoje: 0 }
  } finally {
    contandoDestinatarios.value = false
  }
}

function fecharModal() {
  animandoFechamento.value = true
  setTimeout(() => {
    animandoFechamento.value = false
    emit('fechar')
  }, 200)
}

async function salvar() {
  salvando.value = true
  try {
    emit('salvar', { ...form.value })
  } finally {
    salvando.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 10px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  max-width: 700px;
  width: 95%;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease-out;
}

.modal-fechando {
  animation: slideDown 0.2s ease-in;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideDown {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(20px);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.btn-close {
  background: none;
  border: none;
  font-size: 18px;
  color: #999;
  cursor: pointer;
  padding: 5px;
}

.btn-close:hover {
  color: #333;
}

.steps-indicator {
  display: flex;
  justify-content: center;
  padding: 20px;
  gap: 10px;
  border-bottom: 1px solid #eee;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  opacity: 0.4;
  transition: opacity 0.2s;
}

.step.active {
  opacity: 1;
}

.step-number {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  color: #666;
}

.step.active .step-number {
  background: #4caf50;
  color: white;
}

.step.current .step-number {
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.3);
}

.step-label {
  font-size: 11px;
  color: #666;
}

.modal-body {
  padding: 20px;
  min-height: 300px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  font-size: 14px;
}

.required {
  color: #c62828;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #4caf50;
}

.form-hint {
  display: block;
  font-size: 12px;
  color: #888;
  margin-top: 5px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}

.tipos-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.tipo-card {
  padding: 15px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  text-align: center;
  transition: all 0.2s;
}

.tipo-card:hover {
  border-color: #bbb;
}

.tipo-card.selected {
  background: #f8f8f8;
}

.tipo-card i {
  font-size: 24px;
  margin-bottom: 8px;
  display: block;
}

.tipo-label {
  display: block;
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 4px;
}

.tipo-desc {
  display: block;
  font-size: 11px;
  color: #888;
  line-height: 1.3;
}

.canal-options,
.frequencia-options {
  display: flex;
  gap: 10px;
}

.radio-card {
  flex: 1;
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  text-align: center;
  transition: all 0.2s;
}

.radio-card:hover {
  border-color: #bbb;
}

.radio-card.selected {
  border-color: #4caf50;
  background: #e8f5e9;
}

.radio-card input {
  display: none;
}

.radio-card i {
  font-size: 20px;
  margin-bottom: 5px;
  display: block;
}

.freq-label {
  display: block;
  font-weight: 600;
  font-size: 13px;
}

.freq-desc {
  display: block;
  font-size: 11px;
  color: #888;
}

.config-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.config-header i {
  font-size: 20px;
}

.time-input {
  max-width: 150px;
}

.info-box {
  display: flex;
  gap: 12px;
  padding: 15px;
  background: #e3f2fd;
  border-radius: 8px;
  color: #1976d2;
}

.info-box i {
  font-size: 18px;
  margin-top: 2px;
}

.info-box p {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
}

/* Filtros section */
.step-title {
  font-size: 18px;
  margin: 0 0 10px 0;
  color: #333;
}

.step-description {
  color: #666;
  margin-bottom: 20px;
  font-size: 14px;
}

.filtros-sections {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filtro-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.filtro-header label {
  margin-bottom: 0 !important;
}

.filtro-hint {
  font-size: 11px;
  color: #999;
  font-style: italic;
}

.btn-limpar-filtro {
  background: none;
  border: none;
  color: #e53935;
  font-size: 12px;
  cursor: pointer;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
}

.btn-limpar-filtro:hover {
  background: #fce4ec;
}

.chips-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.chips-uf {
  max-height: 110px;
  overflow-y: auto;
  padding: 4px 2px;
}

.chip {
  padding: 5px 12px;
  border: 1px solid #ddd;
  border-radius: 20px;
  background: #f5f5f5;
  color: #555;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}

.chip:hover {
  border-color: #4caf50;
  background: #f1f8e9;
}

.chip.selected {
  background: #4caf50;
  color: white;
  border-color: #4caf50;
}

.chip-sistema {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.filtros-row {
  display: flex;
  gap: 20px;
}

.filtros-row .form-group {
  flex: 1;
}

.idade-range {
  display: flex;
  align-items: center;
  gap: 10px;
}

.idade-range span {
  color: #666;
  font-size: 14px;
}

.form-input-small {
  width: 80px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  text-align: center;
}

/* Destinatarios count */
.destinatarios-count {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
  padding: 15px;
  background: #e8f5e9;
  border-radius: 8px;
}

.count-box {
  display: flex;
  flex-direction: column;
}

.count-number {
  font-size: 28px;
  font-weight: 700;
  color: #388e3c;
}

.count-label {
  font-size: 13px;
  color: #666;
}

.count-today {
  font-size: 12px;
  color: #888;
}

.count-loading {
  font-size: 16px;
  color: #666;
  font-weight: 500;
}

.count-loading i {
  color: #4caf50;
  margin-right: 6px;
}

.btn-refresh {
  padding: 8px 15px;
  background: white;
  border: 1px solid #388e3c;
  border-radius: 6px;
  color: #388e3c;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-refresh:hover:not(:disabled) {
  background: #f1f8e9;
}

.btn-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.review-title {
  font-size: 16px;
  color: #333;
  margin-bottom: 20px;
}

.review-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}

.review-item {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
}

.review-item.full-width {
  grid-column: span 2;
}

.review-item label {
  display: block;
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  margin-bottom: 5px;
}

.review-item span {
  font-size: 14px;
  color: #333;
}

.tipo-badge,
.canal-badge,
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.canal-badge.email {
  background: #e3f2fd;
  color: #1976d2;
}

.canal-badge.whatsapp {
  background: #e8f5e9;
  color: #388e3c;
}

.status-badge.ativo {
  background: #e8f5e9;
  color: #388e3c;
}

.filtros-resumo {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.filtros-resumo span {
  font-size: 13px;
}

.sem-filtros {
  color: #888;
  font-style: italic;
}

.destino-count {
  font-size: 20px;
  font-weight: 700;
  color: #4caf50;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  border-top: 1px solid #eee;
}

.btn-primary,
.btn-secondary {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn-primary {
  background: #4caf50;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #388e3c;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: #f5f5f5;
  color: #666;
}

.btn-secondary:hover {
  background: #e0e0e0;
}

/* Dark mode */
.dark-mode .modal-content {
  background: #1e1e1e;
}

.dark-mode .modal-header,
.dark-mode .steps-indicator,
.dark-mode .modal-footer {
  border-color: #444;
}

.dark-mode .modal-header h2,
.dark-mode .form-group label,
.dark-mode .config-header {
  color: #f1f1f1;
}

.dark-mode .form-input,
.dark-mode .select-filtro {
  background: #2a2a2a;
  border-color: #444;
  color: #f1f1f1;
}

.dark-mode .tipo-card,
.dark-mode .radio-card,
.dark-mode .chip {
  border-color: #444;
  background: #2a2a2a;
}

.dark-mode .tipo-card.selected,
.dark-mode .radio-card.selected {
  background: #1b4332;
}

.dark-mode .chip {
  background: #2a2a2a;
  border-color: #444;
  color: #ccc;
}

.dark-mode .chip:hover {
  background: #1b4332;
  border-color: #4caf50;
}

.dark-mode .chip.selected {
  background: #388e3c;
  border-color: #4caf50;
  color: white;
}

.dark-mode .filtro-hint {
  color: #666;
}

.dark-mode .btn-limpar-filtro {
  color: #ef5350;
}

.dark-mode .btn-limpar-filtro:hover {
  background: #3e2723;
}

.dark-mode .form-input-small {
  background: #2a2a2a;
  border-color: #444;
  color: #f1f1f1;
}

.dark-mode .review-item {
  background: #2a2a2a;
}

.dark-mode .review-item span {
  color: #f1f1f1;
}

.dark-mode .destinatarios-count {
  background: #1b4332;
}

.dark-mode .count-number {
  color: #81c784;
}

.dark-mode .btn-refresh {
  background: #252525;
  border-color: #4caf50;
  color: #81c784;
}

.dark-mode .step-title {
  color: #f1f1f1;
}

.dark-mode .step-description {
  color: #aaa;
}

@media (max-width: 768px) {
  .modal-content {
    width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .tipos-grid {
    grid-template-columns: 1fr;
  }

  .review-grid {
    grid-template-columns: 1fr;
  }

  .review-item.full-width {
    grid-column: span 1;
  }

  .steps-indicator {
    padding: 15px 10px;
  }

  .step-label {
    display: none;
  }

  .filtros-row {
    flex-direction: column;
  }

  .destinatarios-count {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }

  .btn-refresh {
    width: 100%;
    justify-content: center;
  }
}
</style>
